import { useAuth } from '@/providers/auth';
import { GlobalHeader } from '@/components/GlobalHeader';
import { GlobalNavigation } from '@/components/GlobalNavigation';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { Flex } from '@televet/kibble-ui/chakra';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { UserProvider } from '@/providers/user';
import { HostProviders } from '@/lib/hostProviders';

import { SideDrawer } from '@/components/SideDrawer/SideDrawer';
import { AddTaskDialog } from '@/components/AddTaskDialog';
import { HelpDialog } from '@/components/HelpDialog';
import { ScribeWindowHost } from '@/components/Scribe/ScribeWindowHost';
import { useSideDrawer } from '@/hooks/state/useSideDrawer';
import { useRef } from 'react';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context, location }) => {
    const { isAuthenticated } = await context.auth.awaitAuthState();
    if (!isAuthenticated) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { isLoading, isAuthenticated } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isOpen } = useSideDrawer();

  if (isLoading || !isAuthenticated) {
    return null;
  }

  return (
    <UserProvider>
      <HostProviders>
        <Flex className="AuthenticatedLayout" direction="column" h="100dvh" w="100%">
          <ImpersonationBanner />
          <Flex flex="1 1 auto" overflow="hidden" h="100%">
            <GlobalNavigation />
            <Flex
              ref={containerRef}
              className="RootLayout__Content"
              flex="1 1 auto"
              direction="column"
              align="stretch"
              bg="#FAF8F2"
              overflow="hidden"
              position="relative"
            >
              {/* SideDrawer offsets itself by HEADER_HEIGHT against this container, so the
                  header must stay the first child here — see SideDrawer's Positioner. */}
              <GlobalHeader />
              <Flex flex="1 1 auto" direction="row" overflow="hidden" position="relative">
                <Flex
                  flex="1 1 auto"
                  direction="column"
                  overflow="auto"
                  minW={0}
                  style={{
                    marginRight: isOpen ? '400px' : '0',
                    transition: 'margin-right 0.3s ease-in-out',
                  }}
                >
                  <Outlet />
                </Flex>
                <SideDrawer containerRef={containerRef} />
                <AddTaskDialog />
                <HelpDialog />
                <ScribeWindowHost />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </HostProviders>
    </UserProvider>
  );
}
